import React, { useContext, useEffect, useState, useCallback } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { getUserRole } from '../../utils/authUtils';
import apiClient from '../../api/apiClient';
import agentStatusWebSocketService from '../../services/agentStatusWebSocket';
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa';

// Keep in sync with BusinessLogic.Utilities.AdminRoles.All (Logic/Utilities/AdminRoles.cs) —
// the backend single source of truth for this role set (used by SystemAlertsController's
// [Authorize] and AgentStatusHub's group membership). JS/C# can't share a literal constant, so
// this list must be updated by hand if the backend one ever changes.
const ADMIN_ROLES = ['SuperAdmin', 'Owner', 'Admin'];

/**
 * Persistent, per-user-dismissible banner for operational alerts (e.g. a stuck WorkerService
 * RabbitMQ consumer). Fetches once on mount so an admin who wasn't connected when the alert
 * fired still sees it, then stays live via SignalR for anyone already logged in. Dismissal is
 * per-user (see SystemAlertDismissal on the backend) — one admin dismissing it does not hide it
 * from any other admin.
 */
const SystemAlertBanner = () => {
	const { state: authState } = useContext(AuthContext);
	const [alerts, setAlerts] = useState([]);

	const isAdmin = authState.user && ADMIN_ROLES.includes(getUserRole(authState.user));

	useEffect(() => {
		if (!isAdmin) return;

		let isMounted = true;

		apiClient.get('/api/v1.0/systemalerts/active')
			.then((response) => {
				if (!isMounted) return;
				const fetched = response.data || [];
				// Merge rather than replace — a SignalR push can land while this request is
				// still in flight, and this response won't include it since it predates the row.
				setAlerts((prev) => {
					const byId = new Map(fetched.map((a) => [a.id, a]));
					prev.forEach((a) => {
						if (!byId.has(a.id)) byId.set(a.id, a);
					});
					return Array.from(byId.values());
				});
			})
			.catch(() => {
				// Non-critical — the banner simply won't show until the next successful fetch
				// or a live SignalR push.
			});

		return () => {
			isMounted = false;
		};
	}, [isAdmin]);

	useEffect(() => {
		if (!isAdmin) return;

		const unsubscribe = agentStatusWebSocketService.subscribe('workerHealthAlertRaised', (alert) => {
			setAlerts((prev) => (prev.some((a) => a.id === alert.id) ? prev : [alert, ...prev]));
		});

		return unsubscribe;
	}, [isAdmin]);

	const dismiss = useCallback((alertId) => {
		// Optimistic — the dismissal is idempotent server-side, so a failed request just means
		// the banner reappears on the next fetch/push, not a stuck or duplicated dismissal.
		setAlerts((prev) => prev.filter((a) => a.id !== alertId));
		apiClient.post(`/api/v1.0/systemalerts/${alertId}/dismiss`).catch(() => {});
	}, []);

	if (!isAdmin || alerts.length === 0) return null;

	return (
		<div className="system-alert-banner-stack">
			{alerts.map((alert) => (
				<div className="system-alert-banner" key={alert.id} role="alert">
					<FaExclamationTriangle className="system-alert-banner__icon" />
					<span className="system-alert-banner__message">{alert.message}</span>
					<button
						type="button"
						className="system-alert-banner__dismiss"
						onClick={() => dismiss(alert.id)}
						aria-label="Dismiss alert"
					>
						<FaTimes />
					</button>
				</div>
			))}
		</div>
	);
};

export default SystemAlertBanner;
